import React, { useState, useEffect } from 'react';
import HSLColorPicker from './HSLColorPicker';

const LayerStylePanel = ({ map, layer, isOpen, onClose }) => {
  const [styleSettings, setStyleSettings] = useState({
    fillColor: '#007cbf',
    fillOpacity: 0.3,
    strokeColor: '#007cbf',
    strokeWidth: 2,
    strokeOpacity: 1.0,
    pointRadius: 6,
    pointStrokeWidth: 2,
    pointStrokeColor: '#ffffff',
    pointOpacity: 1.0,
    rasterOpacity: 1.0
  });

  useEffect(() => {
    if (isOpen && map && layer) {
      // Get current style from the map layer
      getCurrentLayerStyle();
    }
  }, [isOpen, map, layer]);

  const getCurrentMapSettings = () => {
    if (!map || !layer) return styleSettings;

    const layerId = `layer-${layer.id}`;
    const fillLayerId = `${layerId}-fill`;
    const strokeLayerId = `${layerId}-stroke`;

    try {
      // Check if this is a raster layer first
      if (layer.geometry_type === 'Raster' || layer.type === 'raster' || layer.data_type === 'raster') {
        if (map.getLayer(layerId)) {
          const rasterOpacity = map.getPaintProperty(layerId, 'raster-opacity') || 1.0;
          return {
            ...styleSettings,
            rasterOpacity: rasterOpacity
          };
        }
        return styleSettings;
      }

      // Get current paint properties based on geometry type
      switch (layer.geometry_type?.toLowerCase()) {
        case 'point':
        case 'multipoint':
          if (map.getLayer(layerId)) {
            const circleColor = map.getPaintProperty(layerId, 'circle-color') || '#007cbf';
            const circleRadius = map.getPaintProperty(layerId, 'circle-radius') || 6;
            const circleStrokeColor = map.getPaintProperty(layerId, 'circle-stroke-color') || '#ffffff';
            const circleStrokeWidth = map.getPaintProperty(layerId, 'circle-stroke-width') || 2;
            const circleOpacity = map.getPaintProperty(layerId, 'circle-opacity') || 1.0;
            
            return {
              ...styleSettings,
              fillColor: circleColor,
              pointRadius: circleRadius,
              pointStrokeColor: circleStrokeColor,
              pointStrokeWidth: circleStrokeWidth,
              pointOpacity: circleOpacity
            };
          }
          break;
          
        case 'linestring':
        case 'multilinestring':
          if (map.getLayer(layerId)) {
            const lineColor = map.getPaintProperty(layerId, 'line-color') || '#007cbf';
            const lineWidth = map.getPaintProperty(layerId, 'line-width') || 2;
            const lineOpacity = map.getPaintProperty(layerId, 'line-opacity') || 1.0;
            
            return {
              ...styleSettings,
              strokeColor: lineColor,
              strokeWidth: lineWidth,
              strokeOpacity: lineOpacity
            };
          }
          break;
          
        case 'polygon':
        case 'multipolygon':
          const result = { ...styleSettings };
          
          if (map.getLayer(fillLayerId)) {
            const fillColor = map.getPaintProperty(fillLayerId, 'fill-color') || '#007cbf';
            const fillOpacity = map.getPaintProperty(fillLayerId, 'fill-opacity') || 0.3;
            
            result.fillColor = fillColor;
            result.fillOpacity = fillOpacity;
          }
          if (map.getLayer(strokeLayerId)) {
            const strokeColor = map.getPaintProperty(strokeLayerId, 'line-color') || '#007cbf';
            const strokeWidth = map.getPaintProperty(strokeLayerId, 'line-width') || 2;
            const strokeOpacity = map.getPaintProperty(strokeLayerId, 'line-opacity') || 1.0;
            
            result.strokeColor = strokeColor;
            result.strokeWidth = strokeWidth;
            result.strokeOpacity = strokeOpacity;
          }
          return result;
      }
    } catch (error) {
      console.error('Error getting current map settings:', error);
    }
    
    return styleSettings;
  };

  const getCurrentLayerStyle = () => {
    if (!map || !layer) return;
    
    const currentSettings = getCurrentMapSettings();
    setStyleSettings(currentSettings);
  };

  const applyStyleWithSettings = (settings) => {
    if (!map || !layer) return;

    const layerId = `layer-${layer.id}`;
    const fillLayerId = `${layerId}-fill`;
    const strokeLayerId = `${layerId}-stroke`;

    try {
      // Handle raster layers first
      if (layer.geometry_type === 'Raster' || layer.type === 'raster' || layer.data_type === 'raster') {
        if (map.getLayer(layerId)) {
          map.setPaintProperty(layerId, 'raster-opacity', settings.rasterOpacity);
        }
        return;
      }

      switch (layer.geometry_type?.toLowerCase()) {
        case 'point':
        case 'multipoint':
          if (map.getLayer(layerId)) {
            map.setPaintProperty(layerId, 'circle-color', settings.fillColor);
            map.setPaintProperty(layerId, 'circle-radius', settings.pointRadius);
            map.setPaintProperty(layerId, 'circle-stroke-color', settings.pointStrokeColor);
            map.setPaintProperty(layerId, 'circle-stroke-width', settings.pointStrokeWidth);
            map.setPaintProperty(layerId, 'circle-opacity', settings.pointOpacity);
          }
          break;
          
        case 'linestring':
        case 'multilinestring':
          if (map.getLayer(layerId)) {
            map.setPaintProperty(layerId, 'line-color', settings.strokeColor);
            map.setPaintProperty(layerId, 'line-width', settings.strokeWidth);
            map.setPaintProperty(layerId, 'line-opacity', settings.strokeOpacity);
          }
          break;
          
        case 'polygon':
        case 'multipolygon':
          if (map.getLayer(fillLayerId)) {
            map.setPaintProperty(fillLayerId, 'fill-color', settings.fillColor);
            map.setPaintProperty(fillLayerId, 'fill-opacity', settings.fillOpacity);
          }
          if (map.getLayer(strokeLayerId)) {
            map.setPaintProperty(strokeLayerId, 'line-color', settings.strokeColor);
            map.setPaintProperty(strokeLayerId, 'line-width', settings.strokeWidth);
            map.setPaintProperty(strokeLayerId, 'line-opacity', settings.strokeOpacity);
          }
          break;
      }
    } catch (error) {
      console.error('Error applying style:', error);
    }
  };

  const applyStyle = () => {
    applyStyleWithSettings(styleSettings);
  };

  const resetStyle = () => {
    const defaultSettings = {
      fillColor: '#007cbf',
      fillOpacity: 0.3,
      strokeColor: '#007cbf',
      strokeWidth: 2,
      strokeOpacity: 1.0,
      pointRadius: 6,
      pointStrokeWidth: 2,
      pointStrokeColor: '#ffffff',
      pointOpacity: 1.0,
      rasterOpacity: 1.0
    };
    
    setStyleSettings(defaultSettings);
    applyStyleWithSettings(defaultSettings);
  };

  const handleStyleChange = (property, value) => {
    // Read current values from the map to ensure we don't override other properties
    const currentMapSettings = getCurrentMapSettings();
    
    const newSettings = {
      ...currentMapSettings,
      [property]: value
    };
    
    setStyleSettings(newSettings);
    
    // Apply style changes immediately (live preview)
    applyStyleWithSettings(newSettings);
  };

  const renderStyleControls = () => {
    if (!layer) return null;

    const geometryType = layer.geometry_type?.toLowerCase();
    const isRaster = layer.geometry_type === 'Raster' || layer.type === 'raster' || layer.data_type === 'raster';

    return (
      <div className="style-controls">
        {isRaster && (
          <>
            <div className="style-control">
              <label>Opacity:</label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={styleSettings.rasterOpacity}
                onChange={(e) => handleStyleChange('rasterOpacity', parseFloat(e.target.value))}
              />
              <span>{Math.round(styleSettings.rasterOpacity * 100)}%</span>
            </div>
          </>
        )}
        
        {(geometryType === 'point' || geometryType === 'multipoint') && (
          <>
            <HSLColorPicker
              label="Point Color"
              value={styleSettings.fillColor}
              onChange={(color) => handleStyleChange('fillColor', color)}
            />
            <div className="style-control">
              <label>Point Size:</label>
              <input
                type="range"
                min="1"
                max="20"
                value={styleSettings.pointRadius}
                onChange={(e) => handleStyleChange('pointRadius', parseInt(e.target.value))}
              />
              <span>{styleSettings.pointRadius}px</span>
            </div>
            <HSLColorPicker
              label="Border Color"
              value={styleSettings.pointStrokeColor}
              onChange={(color) => handleStyleChange('pointStrokeColor', color)}
            />
            <div className="style-control">
              <label>Border Width:</label>
              <input
                type="range"
                min="0"
                max="10"
                value={styleSettings.pointStrokeWidth}
                onChange={(e) => handleStyleChange('pointStrokeWidth', parseInt(e.target.value))}
              />
              <span>{styleSettings.pointStrokeWidth}px</span>
            </div>
            <div className="style-control">
              <label>Opacity:</label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={styleSettings.pointOpacity}
                onChange={(e) => handleStyleChange('pointOpacity', parseFloat(e.target.value))}
              />
              <span>{Math.round(styleSettings.pointOpacity * 100)}%</span>
            </div>
          </>
        )}

        {(geometryType === 'linestring' || geometryType === 'multilinestring') && (
          <>
            <HSLColorPicker
              label="Line Color"
              value={styleSettings.strokeColor}
              onChange={(color) => handleStyleChange('strokeColor', color)}
            />
            <div className="style-control">
              <label>Line Width:</label>
              <input
                type="range"
                min="1"
                max="10"
                value={styleSettings.strokeWidth}
                onChange={(e) => handleStyleChange('strokeWidth', parseInt(e.target.value))}
              />
              <span>{styleSettings.strokeWidth}px</span>
            </div>
            <div className="style-control">
              <label>Opacity:</label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={styleSettings.strokeOpacity}
                onChange={(e) => handleStyleChange('strokeOpacity', parseFloat(e.target.value))}
              />
              <span>{Math.round(styleSettings.strokeOpacity * 100)}%</span>
            </div>
          </>
        )}

        {(geometryType === 'polygon' || geometryType === 'multipolygon') && (
          <>
            <HSLColorPicker
              label="Fill Color"
              value={styleSettings.fillColor}
              onChange={(color) => handleStyleChange('fillColor', color)}
            />
            <div className="style-control">
              <label>Fill Opacity:</label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={styleSettings.fillOpacity}
                onChange={(e) => handleStyleChange('fillOpacity', parseFloat(e.target.value))}
              />
              <span>{Math.round(styleSettings.fillOpacity * 100)}%</span>
            </div>
            <HSLColorPicker
              label="Border Color"
              value={styleSettings.strokeColor}
              onChange={(color) => handleStyleChange('strokeColor', color)}
            />
            <div className="style-control">
              <label>Border Width:</label>
              <input
                type="range"
                min="0"
                max="10"
                value={styleSettings.strokeWidth}
                onChange={(e) => handleStyleChange('strokeWidth', parseInt(e.target.value))}
              />
              <span>{styleSettings.strokeWidth}px</span>
            </div>
            <div className="style-control">
              <label>Border Opacity:</label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={styleSettings.strokeOpacity}
                onChange={(e) => handleStyleChange('strokeOpacity', parseFloat(e.target.value))}
              />
              <span>{Math.round(styleSettings.strokeOpacity * 100)}%</span>
            </div>
          </>
        )}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="layer-style-overlay">
      <div className="layer-style-panel">
        <div className="style-panel-header">
          <h3>Style Layer: {layer?.name}</h3>
          <button className="btn-close-style" onClick={onClose}>
            ✕
          </button>
        </div>
        
        <div className="style-panel-content">
          <div className="layer-info-section">
            <p><strong>Geometry:</strong> {layer?.geometry_type}</p>
            <p><strong>Features:</strong> {layer?.feature_count}</p>
          </div>

          {renderStyleControls()}

          <div className="style-panel-actions">
            <button
              className="btn-reset-style"
              onClick={resetStyle}
            >
              Reset to Default
            </button>
            <button
              className="btn-close-style-secondary"
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LayerStylePanel;